/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '@/components/Icon';
import { Button } from '@/design-system';
import { RELEASE_NOTES, type ReleaseTag } from '@/features/releaseNotes/data';
import { ShellDialog } from './ShellDialog';

const TAG_META: Record<ReleaseTag, { labelKey: string; icon: IconName }> = {
  feature: { labelKey: 'releaseNotes.tags.feature', icon: 'auto_awesome' },
  improvement: { labelKey: 'releaseNotes.tags.improvement', icon: 'arrow_upward' },
  fix: { labelKey: 'releaseNotes.tags.fix', icon: 'check' },
};

const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/** What's new dialog (versioned release notes, newest first). */
export function ReleaseNotesModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <ShellDialog
      onClose={onClose}
      icon="auto_awesome"
      title={t('releaseNotes.title')}
      description={t('releaseNotes.subtitle')}
      className="rn-modal"
      footer={
        <Button type="button" onClick={onClose}>
          {t('releaseNotes.gotIt')}
        </Button>
      }
    >
      <div className="rn-body">
        {RELEASE_NOTES.map((note, i) => (
          <section key={note.version} className="rn-rel">
            <div className="rn-rel-head">
              <span className="rn-ver">v{note.version}</span>
              {i === 0 && <span className="rn-latest">{t('releaseNotes.latest')}</span>}
              <span className="rn-date">{fmtDate(note.date)}</span>
            </div>
            <h3 className="rn-title">{t(note.title)}</h3>
            {note.intro && <p className="rn-note">{t(note.intro)}</p>}
            <ul className="rn-list">
              {note.highlights.map((h) => (
                <li key={h.text} className="rn-item">
                  <span className={`rn-tag rn-tag-${h.tag}`}>
                    <Icon name={TAG_META[h.tag].icon} size={14} />
                    {t(TAG_META[h.tag].labelKey)}
                  </span>
                  <span className="rn-text">{t(h.text)}</span>
                </li>
              ))}
            </ul>
            {note.outro && <p className="rn-note">{t(note.outro)}</p>}
          </section>
        ))}
      </div>
    </ShellDialog>
  );
}
